import React, { useState, useEffect } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg?react';
import './index.css';

// eslint-disable-next-line no-shadow
export enum SortOption {
  BY_FARM = 'BY_FARM',
  BY_CROP = 'BY_CROP'
}

interface PropertySortingDropdownProps {
  onSortChange?: (sortOption: SortOption) => void;
  defaultSort?: SortOption;
}

const PropertySortingDropdown: React.FC<PropertySortingDropdownProps> = ({
  onSortChange,
  defaultSort = SortOption.BY_FARM
}) => {
  const [selectedSort, setSelectedSort] = useState<SortOption>(defaultSort);

  // Sync internal state with defaultSort prop changes
  useEffect(() => {
    setSelectedSort(defaultSort);
  }, [defaultSort]);

  const renderCheckmark = (isActive: boolean) => (
    <svg
      width="20"
      height="19"
      viewBox="0 0 25 24"
      fill="none"
      className="shrink-0 mr-1 ml-2"
    >
      <path
        d="M8.8273 15.8769L4.6573 11.7069L3.2373 13.1169L8.8273 18.7069L20.8273 6.70687L19.4173 5.29688L8.8273 15.8769Z"
        fill={isActive ? '#0092E4' : 'transparent'}
      />
    </svg>
  );

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const sortOption = key as SortOption;
    setSelectedSort(sortOption);
    onSortChange?.(sortOption);
  };

  const sortItems: MenuProps['items'] = [
    {
      key: SortOption.BY_FARM,
      label: (
        <div
          className={`flex items-center w-full h-full ant-dropdown-menu-item ${
            selectedSort === SortOption.BY_FARM
              ? 'ant-dropdown-menu-item selected-item'
              : ''
          }`}
        >
          {renderCheckmark(selectedSort === SortOption.BY_FARM)}
          <span
            className={`px-2 py-1 ${
              selectedSort === SortOption.BY_FARM ? 'font-semibold' : ''
            }`}
          >
            Group by farm
          </span>
        </div>
      )
    },
    {
      key: SortOption.BY_CROP,
      label: (
        <div
          className={`flex items-center w-full h-full ant-dropdown-menu-item ${
            selectedSort === SortOption.BY_CROP
              ? 'ant-dropdown-menu-item selected-item'
              : ''
          }`}
        >
          {renderCheckmark(selectedSort === SortOption.BY_CROP)}
          <span
            className={`px-2 py-1 ${
              selectedSort === SortOption.BY_CROP ? 'font-semibold' : ''
            }`}
          >
            Group by crop
          </span>
        </div>
      )
    }
  ];

  return (
    <Tooltip title="Group By" placement="top" color="#232630">
      <Dropdown
        menu={{ items: sortItems, onClick: handleMenuClick }}
        trigger={['click']}
        placement="bottomRight"
        overlayClassName="property-sorting-dropdown"
      >
        <button
          type="button"
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 bg-white border border-neutral-30"
          aria-label="Sort properties"
        >
          <SortIcon className="w-4 h-4" />
        </button>
      </Dropdown>
    </Tooltip>
  );
};

export default PropertySortingDropdown;
